import React, { useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { cn } from 'lib/utils';

export interface Tab {
    id: string;
    label: string;
    icon?: React.ReactNode;
    content?: React.ReactNode;
    disabled?: boolean;
    badge?: string | number;
}

export interface TabsProps {
    tabs: Tab[];
    activeTab?: string;
    onTabChange?: (tabId: string) => void;
    defaultTab?: string;
    useQueryParam?: boolean;
    queryParamName?: string;
    variant?: 'default' | 'pills' | 'underline';
    className?: string;
    renderContent?: (tabId: string) => React.ReactNode;
    children?: React.ReactNode;
}

/**
 * URL 동기화 + 3가지 variant 유지하는 호환 래퍼.
 * - default/underline: 하단 보더 4px Navy
 * - pills: 활성 탭 navy-50 배경 + primary 텍스트 (보라 톤 제거)
 */
const Tabs: React.FC<TabsProps> = ({
    tabs,
    activeTab: controlledActiveTab,
    onTabChange,
    defaultTab,
    useQueryParam = true,
    queryParamName = 'tab',
    variant = 'default',
    className = '',
    renderContent,
    children,
}) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [internalActiveTab, setInternalActiveTab] = React.useState(
        defaultTab || tabs[0]?.id || ''
    );

    const activeTab = controlledActiveTab !== undefined ? controlledActiveTab : internalActiveTab;

    const handleTabClick = useCallback(
        (tabId: string) => {
            const tab = tabs.find((t) => t.id === tabId);
            if (tab?.disabled) return;

            if (controlledActiveTab === undefined) {
                setInternalActiveTab(tabId);
            }

            if (useQueryParam) {
                const newParams = new URLSearchParams(searchParams);
                newParams.set(queryParamName, tabId);
                setSearchParams(newParams, { replace: true });
            }

            onTabChange?.(tabId);
        },
        [tabs, controlledActiveTab, useQueryParam, searchParams, queryParamName, setSearchParams, onTabChange]
    );

    useEffect(() => {
        if (!useQueryParam) return;
        const tabParam = searchParams.get(queryParamName);
        const validTab = tabs.find((tab) => tab.id === tabParam);
        if (validTab && !validTab.disabled) {
            setInternalActiveTab(validTab.id);
            onTabChange?.(validTab.id);
        } else if (!tabParam && defaultTab) {
            handleTabClick(defaultTab);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, queryParamName, useQueryParam]);

    const activeTabData = tabs.find((tab) => tab.id === activeTab);

    const isPills = variant === 'pills';

    return (
        <div className={cn('w-full', className)}>
            {/*
              overflow-x-auto는 overflow-y도 auto로 만든다. 활성 밑줄을 -mb-px로 겹치면 1px 세로 넘침 → 세로 스크롤바가 생김.
              그래서 하단 기준선은 border 대신 inset shadow로 그리고(자식 border-b-2가 위를 덮음), 세로 넘침은 숨긴다.
            */}
            <div
                role="tablist"
                className={cn(
                    'flex items-center gap-1 overflow-x-auto overflow-y-hidden',
                    !isPills && 'gap-6 shadow-[inset_0_-1px_0_hsl(var(--border))]'
                )}
            >
                {tabs.map((tab) => {
                    const active = activeTab === tab.id;
                    return (
                        <button
                            key={tab.id}
                            type="button"
                            role="tab"
                            aria-selected={active}
                            disabled={tab.disabled}
                            onClick={() => handleTabClick(tab.id)}
                            className={cn(
                                'inline-flex items-center gap-2 transition-colors duration-fast whitespace-nowrap',
                                'focus-visible:outline-none focus-visible:text-foreground',
                                'disabled:cursor-not-allowed disabled:text-neutral-400',
                                isPills
                                    ? cn(
                                          'h-8 px-3 rounded text-sm font-medium',
                                          active
                                              ? 'bg-navy-50 text-primary'
                                              : 'text-neutral-500 hover:bg-muted hover:text-foreground'
                                      )
                                    : cn(
                                          'h-10 px-1 text-md font-medium border-b-2',
                                          active
                                              ? 'border-primary text-foreground'
                                              : 'border-transparent text-neutral-500 hover:text-foreground'
                                      )
                            )}
                        >
                            {tab.icon && (
                                <span className="inline-flex items-center [&_svg]:h-4 [&_svg]:w-4">
                                    {tab.icon}
                                </span>
                            )}
                            <span>{tab.label}</span>
                            {tab.badge !== undefined && (
                                <span
                                    className={cn(
                                        'inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full text-xs font-medium tabular-nums',
                                        active
                                            ? 'bg-primary text-primary-foreground'
                                            : 'bg-neutral-100 text-neutral-700'
                                    )}
                                >
                                    {tab.badge}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            <div className="mt-4">
                {children || (renderContent ? renderContent(activeTab) : activeTabData?.content)}
            </div>
        </div>
    );
};

export default Tabs;
